import React, { useMemo } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { whitelistDomainSchema } from './validationSchema';
import { Box, Button, TextField, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import {
  AddWhitelistDomainModalProps,
  WhitelistForm as FormValues,
} from './types';
import { ModalContainer, StyledPaper } from '../../UserRequestsStyles';

export const AddWhitelistDomainModal = ({
  open,
  onClose,
  onSubmit,
  existingDomains,
}: AddWhitelistDomainModalProps) => {
  const { control, handleSubmit, watch, setError, clearErrors, reset } =
    useForm({
      resolver: yupResolver(whitelistDomainSchema),
      defaultValues: { domain: '', note: '' },
    });

  const watchedDomain = watch('domain');
  const { enqueueSnackbar } = useSnackbar();

  const normalizeDomain = (value: string) => value.trim().toLowerCase();

  const isDuplicate = useMemo(() => {
    if (!watchedDomain) return false;
    const normalized = normalizeDomain(watchedDomain);
    return existingDomains
      .map(normalizeDomain)
      .some(domain => domain === normalized);
  }, [existingDomains, watchedDomain]);

  const submit: SubmitHandler<FormValues> = values => {
    clearErrors();

    if (isDuplicate) {
      setError('domain', {
        type: 'manual',
        message: 'Domain already whitelisted',
      });
      enqueueSnackbar('Domain already whitelisted', { variant: 'warning' });
      return;
    }

    onSubmit({
      domain: normalizeDomain(values.domain),
      note: (values.note || '').trim(),
    });
    reset();
  };

  return (
    <ModalContainer open={open} onClose={onClose}>
      <StyledPaper>
        <Box mb={2}>
          <Typography variant="h6">Add Whitelisted Domain</Typography>
        </Box>
        <form onSubmit={handleSubmit(submit)} noValidate>
          <Box display="flex" flexDirection="column" gridGap={8}>
            <Controller
              name="domain"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Domain Name"
                  placeholder="digitalrealty.com"
                  variant="outlined"
                  fullWidth
                  required
                  error={!!error || isDuplicate}
                  helperText={
                    error?.message ||
                    (isDuplicate ? 'Domain already whitelisted' : ' ')
                  }
                />
              )}
            />
            <Controller
              name="note"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Note"
                  placeholder="The reason for adding here"
                  variant="outlined"
                  fullWidth
                  inputProps={{ maxLength: 100 }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Box>
          <Box
            mt={3}
            display="flex"
            justifyContent="flex-end"
            gridColumnGap={24}
          >
            <Button onClick={onClose} variant="text" color="primary">
              Cancel
            </Button>
            <Button type="submit" variant="text" color="primary">
              Add Domain
            </Button>
          </Box>
        </form>
      </StyledPaper>
    </ModalContainer>
  );
};

export default AddWhitelistDomainModal;
